import React from 'react';
import { motion } from 'motion/react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent, HappyMember } from '../content';

function MemberCard({ member, index }: { member: HappyMember; index: number; key?: React.Key }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: BDAY_EASE }}
      whileHover={{ y: -8, scale: 1.04 }}
      className="flex flex-col items-center gap-3 group"
    >
      {/* avatar */}
      <div className="relative">
        <div className="absolute -inset-1.5 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
        <div className="relative w-20 h-20 rounded-full overflow-hidden border-2 border-blue-400/40 shadow-lg shadow-blue-900/60 bg-blue-800/60 flex items-center justify-center">
          {member.photoUrl ? (
            <img
              src={member.photoUrl}
              alt={member.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-3xl">{member.emoji ?? '😊'}</span>
          )}
        </div>
        {/* online-style dot */}
        <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-blue-400 border-2 border-[#0f172a] flex items-center justify-center">
          <span className="text-[8px]">🎉</span>
        </div>
      </div>

      {/* name */}
      <p className="text-blue-100 font-semibold text-sm text-center group-hover:text-white transition-colors">
        {member.name}
      </p>
      <p className="text-blue-400 text-xs">Excited to celebrate!</p>
    </motion.div>
  );
}

export function HappyMembersSection({ content }: { content: BirthdayContent['happyMembers'] }) {
  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 100%)' }}
    >
      {/* background sparkle dots */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-blue-400/30"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
            animate={{ opacity: [0.2, 0.8, 0.2], scale: [1, 1.5, 1] }}
            transition={{ duration: 2 + Math.random() * 3, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      <div className="relative max-w-5xl mx-auto">
        {/* heading */}
        <div className="text-center mb-14">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: BDAY_EASE }}
            className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
          >
            {content.label}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            {content.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-3 text-blue-300 text-sm"
          >
            The amazing people making this day unforgettable
          </motion.p>
        </div>

        {/* members grid */}
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-8 justify-items-center">
          {content.members.slice(0, 6).map((member, i) => (
            <MemberCard key={`${member.name}-${i}`} member={member} index={i} />
          ))}
        </div>

        {/* decorative divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 h-px bg-gradient-to-r from-transparent via-blue-400/40 to-transparent"
        />
      </div>
    </section>
  );
}
